import { useState } from "react";
import axios from "axios";
import { ShieldCheck, GraduationCap, TrendingUp, MapPin, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { provinces } from "../data/provinces";
import { getHead, PHONE, PHONE_TEL, EMAIL, EMAIL_MAILTO } from "../seo/routes";
import { Seo } from "../components/Seo";
import { PageHero, SectionHead, CTABand } from "../components/Blocks";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const roles = [
  { title: "Licensed Security Guard", type: "Full-time & part-time", location: "All provinces & territories" },
  { title: "Mobile Patrol Officer", type: "Full-time", location: "Major metros across Canada" },
  { title: "Concierge Security Officer", type: "Full-time & part-time", location: "Toronto, Montreal, Vancouver, Ottawa" },
  { title: "Event Security Guard", type: "On-call & seasonal", location: "Nationwide" },
  { title: "Fire Watch Guard", type: "On-call", location: "Nationwide" },
  { title: "Site Supervisor", type: "Full-time", location: "Major metros across Canada" },
];

const perks = [
  { icon: ShieldCheck, title: "Professional Standards", text: "Proper uniforms, working equipment, written post orders and supervisors who actually show up. You represent a company that takes the work seriously." },
  { icon: GraduationCap, title: "Licensing Support", text: "New to the industry? We help candidates navigate provincial licensing and training requirements so you can start your security career on solid ground." },
  { icon: TrendingUp, title: "Room to Grow", text: "Guards who perform move up — to specialized posts, mobile patrol, event teams and site supervision across our national operation." },
];

const emptyForm = { full_name: "", email: "", phone: "", province: "", city: "", role: "", licensed: "", message: "" };

const head = getHead("/careers");

export default function Careers() {
  const [form, setForm] = useState(emptyForm);
  const [sending, setSending] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/careers/applications`, form);
      toast.success("Application received — our recruiting team will reach out by email or phone.");
      setForm(emptyForm);
    } catch (err) {
      toast.error("Something went wrong. Please email your application to info@mazesecurity.ca.");
    } finally {
      setSending(false);
    }
  };

  const inputCls = "w-full bg-[#0D0E12] border border-white/15 rounded-sm px-4 py-2.5 text-sm text-gray-100 placeholder-gray-500 focus:outline-none focus:border-[#FF5500] transition-colors";

  return (
    <>
      <Seo title={head.title} description={head.description} path="/careers" />
      <PageHero
        overline="Careers"
        title="Join the Maze Security Team"
        lead="We're hiring licensed security guards — and people ready to become one — across all 13 Canadian provinces and territories. Real supervision, real standards, real shifts."
      />
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16" data-testid="careers-perks">
        <SectionHead overline="Why Maze" title="A Security Career With Backup" />
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {perks.map((p) => (
            <div key={p.title} className="border border-white/10 bg-[#181C28] p-6 rounded-sm" data-testid={`perk-${p.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
              <p.icon className="h-8 w-8 text-[#FF5500] mb-4" />
              <h2 className="font-display text-xl font-bold uppercase tracking-tight text-white">{p.title}</h2>
              <p className="mt-2 text-sm text-gray-400 leading-relaxed">{p.text}</p>
            </div>
          ))}
        </div>
      </section>
      <section className="bg-[#131620] border-y border-white/10" data-testid="open-positions">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <SectionHead overline="Open Roles" title="Positions We're Filling" sub="Apply below — if your city isn't listed, apply anyway. Our coverage follows our people." />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {roles.map((r) => (
              <div key={r.title} className="flex items-center justify-between border border-white/10 bg-[#0D0E12] p-5 rounded-sm" data-testid={`role-${r.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                <div>
                  <h3 className="font-display text-lg font-bold uppercase tracking-tight text-white">{r.title}</h3>
                  <p className="text-xs text-gray-500 mt-1 flex items-center gap-1"><MapPin className="h-3 w-3 text-[#FF5500]" /> {r.location} · {r.type}</p>
                </div>
                <a href="#apply" data-testid={`apply-${r.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`} className="text-xs font-mono uppercase tracking-wider text-[#FF5500] hover:text-white transition-colors shrink-0 ml-4">
                  Apply →
                </a>
              </div>
            ))}
          </div>
        </div>
      </section>
      <section id="apply" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16" data-testid="careers-apply">
        <SectionHead overline="Apply Online" title="Guard Application" sub="Two minutes, no account needed. Prefer email? Send your details and résumé to info@mazesecurity.ca." />
        <form onSubmit={submit} className="max-w-3xl grid grid-cols-1 sm:grid-cols-2 gap-4" data-testid="careers-form">
          <div>
            <label className="block text-xs font-mono uppercase tracking-wider text-gray-400 mb-1.5" htmlFor="cf-name">Full name *</label>
            <input id="cf-name" required data-testid="careers-fullname-input" className={inputCls} value={form.full_name} onChange={set("full_name")} placeholder="Jane Tremblay" />
          </div>
          <div>
            <label className="block text-xs font-mono uppercase tracking-wider text-gray-400 mb-1.5" htmlFor="cf-email">Email *</label>
            <input id="cf-email" type="email" required data-testid="careers-email-input" className={inputCls} value={form.email} onChange={set("email")} placeholder="you@example.ca" />
          </div>
          <div>
            <label className="block text-xs font-mono uppercase tracking-wider text-gray-400 mb-1.5" htmlFor="cf-phone">Phone *</label>
            <input id="cf-phone" required data-testid="careers-phone-input" className={inputCls} value={form.phone} onChange={set("phone")} placeholder="+1 555 000 0000" />
          </div>
          <div>
            <label className="block text-xs font-mono uppercase tracking-wider text-gray-400 mb-1.5" htmlFor="cf-province">Province / Territory *</label>
            <select id="cf-province" required data-testid="careers-province-select" className={inputCls} value={form.province} onChange={set("province")}>
              <option value="">Select…</option>
              {provinces.map((p) => (
                <option key={p.slug} value={p.name}>{p.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-mono uppercase tracking-wider text-gray-400 mb-1.5" htmlFor="cf-city">City</label>
            <input id="cf-city" data-testid="careers-city-input" className={inputCls} value={form.city} onChange={set("city")} placeholder="Toronto" />
          </div>
          <div>
            <label className="block text-xs font-mono uppercase tracking-wider text-gray-400 mb-1.5" htmlFor="cf-role">Position *</label>
            <select id="cf-role" required data-testid="careers-role-select" className={inputCls} value={form.role} onChange={set("role")}>
              <option value="">Select…</option>
              {roles.map((r) => (
                <option key={r.title} value={r.title}>{r.title}</option>
              ))}
            </select>
          </div>
          <div className="sm:col-span-2">
            <label className="block text-xs font-mono uppercase tracking-wider text-gray-400 mb-1.5" htmlFor="cf-licensed">Licence status *</label>
            <select id="cf-licensed" required data-testid="careers-licensed-select" className={inputCls} value={form.licensed} onChange={set("licensed")}>
              <option value="">Select…</option>
              <option value="Yes — valid provincial licence">Yes — I hold a valid provincial security licence</option>
              <option value="In training">I'm completing my training / exam</option>
              <option value="Not yet licensed">Not yet — I need guidance getting licensed</option>
            </select>
          </div>
          <div className="sm:col-span-2">
            <label className="block text-xs font-mono uppercase tracking-wider text-gray-400 mb-1.5" htmlFor="cf-message">Experience & availability</label>
            <textarea id="cf-message" rows={4} data-testid="careers-message-input" className={inputCls} value={form.message} onChange={set("message")} placeholder="Tell us about your experience, availability and the kind of posts you're looking for…" />
          </div>
          <div className="sm:col-span-2">
            <button
              type="submit"
              disabled={sending}
              data-testid="careers-submit-button"
              className="inline-flex items-center gap-2 bg-[#FF5500] hover:bg-[#E04800] disabled:opacity-60 text-white font-semibold px-8 py-3.5 rounded-sm transition-colors duration-200"
            >
              {sending && <Loader2 className="h-4 w-4 animate-spin" />}
              {sending ? "Sending…" : "Submit Application"}
            </button>
            <p className="mt-3 text-xs text-gray-500">
              Prefer direct contact? Call <a href={PHONE_TEL} data-testid="careers-phone-link" className="text-[#FF5500] hover:text-white">{PHONE}</a> or email <a href={EMAIL_MAILTO} data-testid="careers-email-link" className="text-[#FF5500] hover:text-white">{EMAIL}</a>.
            </p>
          </div>
        </form>
      </section>
      <CTABand title="Licensed. Verified. Backed Up." sub="That's the standard we hold ourselves to — and the team you'll be part of." />
    </>
  );
}
